import * as d3 from 'd3'

import {
  MENU_VIEW_MODES,
  DOT_RADIUS,
  DOT_STROKE_WIDTH,
  DOT_STYLE,
  HEATMAP_COLORS,
  HEATMAP_OPACITY,
  OVERLAY_OPACITY,
  HEATMAP_IMAGE_OPACITY,
  SECTION_STYLE
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/constants'
import { sectionTitle } from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/text'

// the id is an export contract: pptx.coffee (getMenuTestParams -> formatSVG) clones this
// node out of the live DOM to build the PPTX composite
export const overlayId = id => `svg-mn-heatmap${id}`

// removal goes through the container node, not a document lookup by id — the view is
// rendered detached (block_base inserts @el after render()) and a document query misses it
export const removeOverlay = container => d3.select(container).selectAll('svg.svg-mn-heatmap').remove()

// viewBox keeps coordinates in original image space; width/height scale the SVG
// to match the scaled image. The style is written as one attribute so it survives
// the export clone (formatSVG), which leaves the page stylesheets behind.
const createOverlaySvg = (container, { id, width, height, scale, mode }) => {
  const opacity = MENU_VIEW_MODES.HEATMAP === mode ? ` opacity: ${HEATMAP_IMAGE_OPACITY};` : ''

  return container
    .append('svg:svg')
    .attr('id', overlayId(id))
    .attr('width', Math.round(width * scale))
    .attr('height', Math.round(height * scale))
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('class', 'svg-mn-heatmap')
    .attr('style', `position: absolute; top: 0; left: 0;${opacity}`)
}

const drawHeatmap = (svg, { heatmap, mode }) => {
  const { contours, levels } = heatmap

  if (!contours || !levels) return

  const minLevel = d3.min(levels)
  const maxLevel = d3.max(levels)

  const colorsStops = HEATMAP_COLORS.map((_, i) => minLevel + (i / (HEATMAP_COLORS.length - 1)) * (maxLevel - minLevel))

  const opacityValues = MENU_VIEW_MODES.HEATMAP === mode ? HEATMAP_OPACITY : OVERLAY_OPACITY

  const getOpacity = d3.scaleLinear().domain([minLevel, maxLevel]).range(opacityValues)
  const getColor = d3.scaleLinear().domain(colorsStops).range(HEATMAP_COLORS)

  const line = d3
    .line()
    .x(d => d.x)
    .y(d => d.y)

  if (!contours.length) return

  contours.forEach(item => {
    const points = item.contour.map(point => ({ x: point.x, y: point.y }))

    svg
      .append('svg:path')
      .attr('d', line(points))
      .attr('fill', `${getColor(item.level)}`)
      .attr('fill-opacity', `${getOpacity(item.level)}`)
  })
}

// click dots on the menu image
const drawDots = (svg, { heatmap, scale }) => {
  if (!heatmap?.points) return

  // fixed screen-pixel size: compensate for viewBox -> element scaling
  const r = DOT_RADIUS / scale
  const sw = DOT_STROKE_WIDTH / scale

  heatmap.points.forEach(point => {
    svg
      .append('svg:circle')
      .attr('cx', point.x)
      .attr('cy', point.y)
      .attr('r', r)
      .attr('style', `${DOT_STYLE} stroke-width: ${sw};`)
  })
}

// section outlines on the plain image (same SVG layer as heatmap / dots)
const drawSections = (svg, { sections }) => {
  const bordered = (sections || []).filter(s => s?.border?.type)
  if (!bordered.length) return

  bordered.forEach(section => {
    const border = section.border
    let shape

    switch (border.type) {
      case 'rect':
        shape = svg
          .append('svg:rect')
          .attr('x', border.x)
          .attr('y', border.y)
          .attr('width', border.width)
          .attr('height', border.height)
        break
      case 'ellipse':
        shape = svg
          .append('svg:ellipse')
          .attr('cx', border.cx)
          .attr('cy', border.cy)
          .attr('rx', border.rx)
          .attr('ry', border.ry)
        break
      case 'polygon':
        shape = svg.append('svg:polygon').attr('points', (border.points || []).map(p => p.join(',')).join(' '))
        break
      default:
        return
    }

    // transparent (not none) fill keeps the whole area hoverable for the native title;
    // inline base stroke survives the export clone (like contours/dots); opacity + hover live in CSS
    shape
      .classed('mn-section-area', true)
      .attr('style', SECTION_STYLE)
      .append('svg:title')
      .text(sectionTitle(section.text || ''))
  })
}

export const drawOverlay = (container, options) => {
  const { mode } = options
  const svg = createOverlaySvg(container, options)

  if (MENU_VIEW_MODES.HEATMAP === mode || MENU_VIEW_MODES.SHEATMAP === mode) {
    drawHeatmap(svg, options)
  } else if (MENU_VIEW_MODES.CLICKS === mode) {
    drawDots(svg, options)
  } else if (MENU_VIEW_MODES.PLAIN === mode) {
    drawSections(svg, options)
  }

  return svg
}
